import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../../components/feature/Footer';
import { getUserRole, sendVerificationEmail, signOutUser, subscribeAuth } from '../../../services/auth';
import { verifyEmailCode } from '../../../services/email';
import { toUserMessage } from '../../../utils/errors';
import { reload } from 'firebase/auth';

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userUid, setUserUid] = useState<string | null>(null);
  const [userVerified, setUserVerified] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const autoSubmittedRef = useRef(false);

  const redirectTo = useMemo(() => {
    if (!redirectParam) return null;
    try { return decodeURIComponent(redirectParam); } catch { return redirectParam; }
  }, [redirectParam]);

  useEffect(() => {
    const unsub = subscribeAuth((u) => {
      setUserEmail(u?.email ?? null);
      setUserUid(u?.uid ?? null);
      setUserVerified(Boolean(u?.emailVerified));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Already verified? Skip ahead.
  useEffect(() => {
    if (!loading && userVerified) {
      void routeAfterVerified();
    }
  }, [loading, userVerified]);

  // 60-second resend cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const routeAfterVerified = async () => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
      return;
    }
    try {
      if (!userUid) { navigate('/', { replace: true }); return; }
      const { role } = await getUserRole(userUid);
      if (role === 'seller') navigate('/seller-dashboard', { replace: true });
      else if (role === 'buyer') navigate('/buyer-dashboard', { replace: true });
      else navigate('/', { replace: true });
    } catch {
      navigate('/', { replace: true });
    }
  };

  const handleDigitChange = (index: number, raw: string) => {
    // Allow pasting the whole 6-digit code into any box.
    const digits = raw.replace(/\D+/g, '');
    if (!digits) {
      const next = [...code];
      next[index] = '';
      setCode(next);
      return;
    }
    const next = [...code];
    for (let i = 0; i < digits.length && index + i < 6; i++) {
      next[index + i] = digits[i];
    }
    setCode(next);
    const moveTo = Math.min(index + digits.length, 5);
    inputsRef.current[moveTo]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D+/g, '').slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setCode(text.split(''));
      inputsRef.current[5]?.focus();
    }
  };

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    const full = code.join('');
    if (full.length === 6 && !autoSubmittedRef.current && !submitting && userEmail) {
      autoSubmittedRef.current = true;
      void submit(full);
    }
    if (full.length < 6) autoSubmittedRef.current = false;
  }, [code, userEmail]);

  const submit = async (override?: string) => {
    setError(null);
    setInfo(null);
    if (!userEmail) { setError('You need to be signed in to verify.'); return; }
    const value = (override ?? code.join('')).trim();
    if (!/^\d{6}$/.test(value)) {
      setError('Enter the 6-digit code we sent to your email.');
      return;
    }
    setSubmitting(true);
    try {
      await verifyEmailCode(userEmail, value, userUid || undefined);
      // Refresh Firebase Auth so client picks up emailVerified=true.
      try {
        const { auth } = await import('../../../lib/firebase');
        if (auth.currentUser) await reload(auth.currentUser);
      } catch {}
      setInfo('Email verified. Redirecting…');
      await routeAfterVerified();
    } catch (e: any) {
      setError(toUserMessage(e) || 'Could not verify the code.');
      autoSubmittedRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setError(null);
    setInfo(null);
    if (cooldown > 0) return;
    setSending(true);
    try {
      await sendVerificationEmail();
      setInfo('A new code has been sent. Check your inbox (and spam folder).');
      setCooldown(60);
    } catch (e: any) {
      setError(toUserMessage(e));
    } finally {
      setSending(false);
    }
  };

  const signOut = async () => {
    await signOutUser();
    navigate('/auth/sign-in', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-fill text-white text-lg"></i>
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Pacifico, serif' }}>
                Campus Market
              </span>
            </Link>
          </div>
        </div>
      </header>
      <main className="max-w-md mx-auto px-4 py-8 md:py-12">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Enter verification code</h1>
          {loading ? (
            <p className="text-gray-600">Loading…</p>
          ) : !userEmail ? (
            <>
              <p className="text-gray-700 mb-6">Please sign in to continue.</p>
              <button onClick={() => navigate('/auth/sign-in')} className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                Go to sign in
              </button>
            </>
          ) : (
            <>
              <p className="text-gray-700 mb-6">
                We sent a 6-digit code to <span className="font-medium">{userEmail}</span>.
                Enter it below to verify your account.
              </p>

              <div className="flex justify-center gap-2 mb-4" onPaste={handlePaste}>
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputsRef.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none disabled:opacity-60"
                    disabled={submitting}
                  />
                ))}
              </div>

              {error && (
                <p className="text-sm text-red-600 mb-3">{error}</p>
              )}
              {info && !error && (
                <p className="text-sm text-green-700 mb-3">{info}</p>
              )}

              <div className="space-y-3">
                <button
                  onClick={() => submit()}
                  disabled={submitting || code.join('').length !== 6}
                  className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  onClick={resend}
                  disabled={sending || cooldown > 0}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending
                    ? 'Sending…'
                    : cooldown > 0
                      ? `Resend code in ${cooldown}s`
                      : 'Resend code'}
                </button>
                <button onClick={signOut} className="w-full px-4 py-3 text-gray-600 hover:text-gray-900 transition-colors">
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EmailVerificationPage;
